import type { LotStorageLocationDto } from '@prospero/shared';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { EquipmentType } from '../../config/equipment-types';
import { useCleaningAreas } from '../../pages/cleaning-areas/hooks/use-cleaning-areas-queries';
import { useEquipmentList } from '../../pages/equipment/hooks/use-equipment-queries';

export interface StoragePlacementOption extends LotStorageLocationDto {
  groupLabel: string;
}

interface StoragePlacementOptionsResult {
  placementOptions: StoragePlacementOption[];
  /** Entrambe le liste arrivano da query che tornano `[]` mentre caricano e in
   * errore: senza questo un elenco vuoto non si distingue da "nessun posto
   * registrato", e chi apre il dialog si vede negare una scelta che esiste. */
  isLoading: boolean;
  isError: boolean;
}

/**
 * Dove un lotto può essere conservato è una domanda sola: frighi, congelatori
 * e aree di pulizia stanno in un unico elenco raggruppato, mai in selettori
 * separati fra cui l'operatore deve indovinare quale compilare.
 */
export function useStoragePlacementOptions(): StoragePlacementOptionsResult {
  const { t } = useTranslation('goodsReceiving');
  const {
    equipmentList,
    isLoading: isLoadingEquipment,
    error: equipmentError,
  } = useEquipmentList();
  const {
    areas: cleaningAreas,
    isLoading: isLoadingCleaningAreas,
    error: cleaningAreasError,
  } = useCleaningAreas();

  const placementOptions = useMemo<StoragePlacementOption[]>(() => {
    const coldStorageOptions = equipmentList
      .filter(
        (equipment) =>
          equipment.type === EquipmentType.Frigorifero ||
          equipment.type === EquipmentType.Congelatore,
      )
      .map((equipment) => ({
        type: 'EQUIPMENT' as const,
        id: equipment.id,
        name: equipment.name,
        groupLabel: t('lotStorage.groupColdStorage'),
      }));
    const cleaningAreaOptions = cleaningAreas.map((cleaningArea) => ({
      type: 'CLEANING_AREA' as const,
      id: cleaningArea.id,
      name: cleaningArea.name,
      groupLabel: t('lotStorage.groupCleaningAreas'),
    }));
    return [...coldStorageOptions, ...cleaningAreaOptions];
  }, [equipmentList, cleaningAreas, t]);

  return {
    placementOptions,
    isLoading: isLoadingEquipment || isLoadingCleaningAreas,
    isError: equipmentError !== null || cleaningAreasError !== null,
  };
}
